/**
 * Deux tableaux de comparaison abonnés / non-abonnés.
 *
 * Des tableaux et non des graphiques : ce sont des paires de nombres à
 * confronter ligne par ligne, une forme graphique n'ajouterait rien et
 * coûterait de la place. Même parti pris que `CustomizerTable`.
 *
 * ── Deux cartes, parce que DEUX POPULATIONS ─────────────────────────────────
 * C'est le point à ne pas rater. La cohorte, ce sont les comptes dont la
 * première action tombe dans la période, suivis ensuite sur tout l'historique.
 * L'activité, ce sont tous les comptes ayant produit une session PENDANT la
 * période, quelle que soit leur date d'arrivée.
 *
 * Les fondre dans un seul tableau ferait comparer des effectifs sans rapport —
 * et on lirait « 40 comptes » à côté de « 900 comptes » sans comprendre
 * pourquoi. Chaque carte annonce donc sa population dans son sous-titre, et les
 * deux affichent leur nombre de comptes en première ligne.
 */

import { SUBSCRIBER_SEGMENT_LABELS } from '@shared/types/stats';

import { formatDecimal, formatDuration, formatInteger, formatPercent } from '../lib/format';
import { COLORS } from '../lib/theme';
import type { SubscriberComparison } from '../types';
import { Card } from './Card';

interface ComparisonRow {
  label: string;
  yes: string;
  no: string;
}

function ComparisonCard({
  title,
  subtitle,
  rows,
  excluded,
  note,
}: {
  title: string;
  subtitle: string;
  rows: readonly ComparisonRow[];
  excluded: number;
  note: string;
}) {
  return (
    <Card title={title} subtitle={subtitle}>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th />
              <th className="num">{SUBSCRIBER_SEGMENT_LABELS.subscribers}</th>
              <th className="num">{SUBSCRIBER_SEGMENT_LABELS.nonSubscribers}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td className="num">{row.yes}</td>
                <td className="num">{row.no}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 12, marginBottom: 0, fontSize: 12, color: COLORS.textMuted }}>
        {note}
        {excluded > 0 && (
          <>
            {' '}
            {formatInteger(excluded)} compte{excluded > 1 ? 's' : ''} écarté
            {excluded > 1 ? 's' : ''} — supprimé{excluded > 1 ? 's' : ''} côté prod, donc
            inclassable{excluded > 1 ? 's' : ''}.
          </>
        )}
      </p>
    </Card>
  );
}

export function SegmentComparisonTables({ data }: { data: SubscriberComparison }) {
  const cohort = data.cohortSubscribers;
  const cohortNo = data.cohortNonSubscribers;
  const activity = data.activitySubscribers;
  const activityNo = data.activityNonSubscribers;

  return (
    <>
      <ComparisonCard
        title="Durée de vie comparée"
        subtitle="Cohorte : comptes dont la 1re action tombe dans la période, suivis sur tout l'historique"
        excluded={data.unknownCohortUsers}
        note="Corrélation, pas causalité — et le segment est lu sur l'état d'aujourd'hui, donc une partie de cet écart précède l'abonnement."
        rows={[
          {
            label: 'Comptes de la cohorte',
            yes: formatInteger(cohort.users),
            no: formatInteger(cohortNo.users),
          },
          {
            label: 'Durée de vie médiane',
            yes: `${formatDecimal(cohort.medianLifetimeDays)} j`,
            no: `${formatDecimal(cohortNo.medianLifetimeDays)} j`,
          },
          {
            label: "Jours d'activité (médiane)",
            yes: formatDecimal(cohort.medianActiveDays),
            no: formatDecimal(cohortNo.medianActiveDays),
          },
          {
            label: 'Actifs un seul jour',
            yes: formatPercent(cohort.singleDayShare),
            no: formatPercent(cohortNo.singleDayShare),
          },
          {
            label: "7 jours d'activité et +",
            yes: formatPercent(cohort.sevenPlusDaysShare),
            no: formatPercent(cohortNo.sevenPlusDaysShare),
          },
        ]}
      />

      <ComparisonCard
        title="Activité comparée"
        subtitle="Tous les comptes ayant produit une session pendant la période — population différente de la cohorte"
        excluded={data.unknownActivityUsers}
        note="Même découpage en sessions que l'onglet Sessions : une nouvelle session après 30 min sans action."
        rows={[
          {
            label: 'Comptes actifs',
            yes: formatInteger(activity.users),
            no: formatInteger(activityNo.users),
          },
          {
            label: 'Sessions',
            yes: formatInteger(activity.sessions),
            no: formatInteger(activityNo.sessions),
          },
          {
            label: 'Sessions par compte',
            yes: formatDecimal(activity.sessionsPerUser),
            no: formatDecimal(activityNo.sessionsPerUser),
          },
          {
            label: 'Durée moyenne de session',
            yes: formatDuration(activity.avgSessionSeconds),
            no: formatDuration(activityNo.avgSessionSeconds),
          },
          {
            label: 'Actions par session',
            yes: formatDecimal(activity.avgActionsPerSession),
            no: formatDecimal(activityNo.avgActionsPerSession),
          },
        ]}
      />
    </>
  );
}
